// ---------------------------------------------------------------------------
// WorkspaceSwitcher — pill in the top bar showing the active workspace
// with a dropdown to switch, create, or manage workspaces.
// ---------------------------------------------------------------------------

import {
  Button,
  Divider,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Add20Filled,
  Add20Regular,
  Checkmark20Regular,
  ChevronDown20Filled,
  ChevronDown20Regular,
  Globe20Regular,
  LockClosed20Regular,
  Settings20Filled,
  Settings20Regular,
  Search20Regular,
  Dismiss20Regular,
} from '@fluentui/react-icons';
import { useEffect, useRef, useState } from 'react';

import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import { useWorkspaceDrawer } from '../../context/WorkspaceDrawerContext.js';
import { useWorkspaces } from '../../hooks/useWorkspaces/useWorkspaces.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  ActionScenario,
  ActionScenarioType,
  PanelRegion,
  PanelType,
} from '../../telemetry/userbi.js';
import { filterWorkspaces } from '../../utils/filterWorkspaces.js';

import { useStyles } from './WorkspaceSwitcher.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const ChevronIcon = bundleIcon(ChevronDown20Filled, ChevronDown20Regular);
const AddIcon = bundleIcon(Add20Filled, Add20Regular);
const SettingsIcon = bundleIcon(Settings20Filled, Settings20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

const PRIVATE_WORKSPACE_LABEL = 'Private';
const PUBLIC_WORKSPACE_LABEL = 'Public';

export function WorkspaceSwitcher() {
  const styles = useStyles();
  const { workspaces, activeWorkspace, isLoading, error } = useWorkspaces();
  const { setActiveName } = useWorkspaceContext();
  const { open: openDrawer } = useWorkspaceDrawer();
  const { recordAction } = useUserBi();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const filteredWorkspaces = filterWorkspaces(workspaces, query);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  const updateQuery = (value: string) => {
    if (query.trim() === '' && value.trim() !== '') {
      recordAction({
        panelType: PanelType.WorkspaceDrawer,
        panelRegion: PanelRegion.Header,
        actionScenario: ActionScenario.SearchWorkspaces,
        actionScenarioType: ActionScenarioType.Search,
        data: { 'Usage.Source': 'switcher' },
      });
    }
    setQuery(value);
  };

  const label = activeWorkspace?.displayName ?? activeWorkspace?.name;
  const showPlaceholder = !isLoading && activeWorkspace === null;

  return (
    <Menu
      open={isOpen}
      onOpenChange={(_event, data) => {
        setQuery('');
        setIsOpen(data.open);
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <button
          className={styles.trigger}
          aria-label="Switch workspace"
          type="button"
        >
          <span
            className={
              showPlaceholder
                ? `${styles.triggerLabel} ${styles.triggerPlaceholder}`
                : styles.triggerLabel
            }
          >
            {isLoading ? 'Loading…' : (label ?? 'Select workspace')}
          </span>
          <ChevronIcon />
        </button>
      </MenuTrigger>

      <MenuPopover className={styles.menuPopover}>
        <div className={styles.menuHeader}>Workspaces</div>
        <Input
          className={styles.search}
          input={{ ref: searchRef, className: styles.searchInput }}
          type="search"
          aria-label="Find workspace"
          placeholder="Find workspace..."
          contentBefore={<Search20Regular />}
          contentAfter={
            query !== '' ? (
              <Button
                appearance="transparent"
                size="small"
                icon={<Dismiss20Regular />}
                aria-label="Clear workspace search"
                onClick={() => {
                  updateQuery('');
                  searchRef.current?.focus();
                }}
              />
            ) : undefined
          }
          value={query}
          onChange={(_event, data) => updateQuery(data.value)}
          onKeyDown={(event) => {
            // Keep typing and caret movement out of the menu's typeahead.
            if (event.key !== 'Escape' && event.key !== 'Tab') {
              event.stopPropagation();
            }
            if (event.target !== searchRef.current) return;
            if (
              event.key === 'ArrowDown' ||
              event.key === 'ArrowUp' ||
              (event.key === 'Enter' && !event.nativeEvent.isComposing)
            ) {
              event.preventDefault();
              const items = listRef.current?.querySelectorAll<HTMLElement>(
                '[role="menuitem"]:not([aria-disabled="true"])',
              );
              const item =
                event.key === 'ArrowUp'
                  ? items?.[items.length - 1]
                  : items?.[0];
              if (event.key === 'Enter') item?.click();
              else item?.focus();
            }
          }}
        />
        <MenuList ref={listRef} className={styles.menuScroll}>
          {isLoading && <MenuItem disabled>Loading workspaces...</MenuItem>}
          {!isLoading && error !== null && (
            <MenuItem disabled>Failed to load workspaces: {error}</MenuItem>
          )}
          {!isLoading && error === null && workspaces.length === 0 && (
            <MenuItem disabled>
              <div className={styles.menuItemInner}>
                <span className={styles.menuItemName}>No workspaces yet</span>
                <span className={styles.menuItemMeta}>
                  Create one to get started
                </span>
              </div>
            </MenuItem>
          )}
          {!isLoading &&
            error === null &&
            workspaces.length > 0 &&
            filteredWorkspaces.length === 0 && (
              <MenuItem disabled>No matching workspaces</MenuItem>
            )}
          {!isLoading &&
            error === null &&
            filteredWorkspaces.map((w) => {
              const isActive = activeWorkspace?.name === w.name;
              const isPrivate = w.accessControl != null;
              const visibility = isPrivate
                ? PRIVATE_WORKSPACE_LABEL
                : PUBLIC_WORKSPACE_LABEL;
              return (
                <MenuItem
                  key={w.name}
                  icon={
                    isActive ? <Checkmark20Regular /> : <span aria-hidden />
                  }
                  onClick={() => {
                    recordAction({
                      panelType: PanelType.WorkspaceDrawer,
                      panelRegion: PanelRegion.Header,
                      actionScenario: ActionScenario.SwitchWorkspace,
                      actionScenarioType: ActionScenarioType.Open,
                      data: { 'Usage.Source': 'switcher' },
                    });
                    setActiveName(w.name);
                  }}
                >
                  <div className={styles.menuItemInner}>
                    <Text className={styles.menuItemNameRow}>
                      <Text className={styles.menuItemName}>
                        {w.displayName ?? w.name}
                      </Text>
                      <Tooltip content={visibility} relationship="description">
                        <Text className={styles.visibilityTooltipTarget}>
                          {isPrivate ? (
                            <LockClosed20Regular
                              className={styles.visibilityIcon}
                              role="img"
                              aria-label={visibility}
                            />
                          ) : (
                            <Globe20Regular
                              className={styles.visibilityIcon}
                              role="img"
                              aria-label={visibility}
                            />
                          )}
                        </Text>
                      </Tooltip>
                    </Text>
                    <span className={styles.menuItemMeta}>{w.name}</span>
                  </div>
                </MenuItem>
              );
            })}
        </MenuList>
        <Divider />
        <MenuList>
          <MenuItem
            icon={<AddIcon />}
            onClick={() => openDrawer({ kind: 'create' })}
          >
            Create workspace
          </MenuItem>
          <MenuItem
            icon={<SettingsIcon />}
            onClick={() => openDrawer({ kind: 'list' })}
          >
            Manage workspaces
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
